import httpClient from '../httpClient';

// ===== INTERFACES =====

export type UserStatus = 'Active' | 'Inactive' | 'Suspended';

export interface User {
  id: string;
  name: string;
  initials: string;
  email: string;
  role: 'student' | 'instructor' | 'admin';
  department: string;
  jobTitle: string;
  status: UserStatus;
  lastLogin: string;
  coursesCompleted: number;
  coursesActive: number;
  createdAt?: string;
  avatar?: string;
  phone?: string;
  location?: string;
  manager?: string;
  joinDate?: string;
  progress?: number;
  /** Reporting hierarchy: who this user directly reports to. */
  reportsToId?: string | null;
  reportsToName?: string | null;
}

export interface UserDetail extends User {
  firstName: string;
  lastName: string;
  roleId: number;
  joinDate: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  organization?: string;
  totalCourses: number;
}

export interface UserStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  newThisMonth: number;
}

export interface DeactivationLog {
  id: string;
  userId: string;
  userName: string;
  department: string;
  deactivatedDate: string;
  deactivatedBy: string;
  reason: string;
  reactivatedAt?: string;
}

export interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateUserDTO {
  firstName: string;
  lastName: string;
  email: string;
  role?: 'student' | 'instructor' | 'admin';
  department?: string;
  jobTitle?: string;
  organizationId?: string | number | string[] | number[];
}

export interface UpdateUserDTO {
  firstName?: string;
  lastName?: string;
  department?: string;
  jobTitle?: string;
  status?: UserStatus;
}

export interface BulkImportUser {
  firstName: string;
  lastName: string;
  email: string;
  role?: string;
  department?: string;
  jobTitle?: string;
}

export interface AdminLog {
  id: number;
  actionType: string;
  actionDescription: string;
  oldValue?: string;
  newValue?: string;
  additionalData?: any;
  createdAt: string;
  adminName: string;
}

// ===== API RESPONSE INTERFACES =====

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  pagination?: Pagination;
}

// ===== SERVICE FUNCTIONS =====

/**
 * Get all users with filtering and pagination
 */
export const getUserDirectory = async (
  params: {
    search?: string;
    status?: string;
    department?: string;
    role?: string;
    page?: number;
    limit?: number;
  } = {}
): Promise<{
  success: boolean;
  data?: User[];
  pagination?: Pagination;
  error?: { message: string } | null;
}> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.department) queryParams.append('department', params.department);
    if (params.role) queryParams.append('role', params.role);
    queryParams.append('page', String(params.page || 1));
    queryParams.append('limit', String(params.limit || 10));

    const response = await httpClient.get<ApiResponse<User[]>>(
      `/admin/users?${queryParams.toString()}`
    );

    return {
      success: true,
      data: response.data || [],
      pagination: response.pagination,
      error: null,
    };
  } catch (error: any) {
    console.error('UsersService: Error fetching users:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch users' },
    };
  }
};

/**
 * Get user statistics
 */
export const getUserStats = async (): Promise<{
  success: boolean;
  data?: UserStats;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<UserStats>>(
      '/admin/users/stats'
    );

    return {
      success: true,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    console.error('UsersService: Error fetching user stats:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch user statistics' },
    };
  }
};

/**
 * Get single user by ID
 */
export const getUserById = async (
  userId: string
): Promise<{
  success: boolean;
  data?: UserDetail;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<UserDetail>>(
      `/admin/users/${userId}`
    );

    return {
      success: true,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    console.error('UsersService: Error fetching user:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch user' },
    };
  }
};

export interface LearningHistoryItem {
  id: string;
  name: string;
  type: string;
  enrolledDate: string;
  totalLessons: number;
  completedLessons: number;
  progress: number;
  status: 'Completed' | 'In Progress' | 'Not Started';
  completedDate?: string;
}

/**
 * Get user's learning history
 */
export const getUserLearningHistory = async (
  userId: string
): Promise<{
  success: boolean;
  data?: LearningHistoryItem[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<LearningHistoryItem[]>>(
      `/admin/users/${userId}/learning-history`
    );

    return {
      success: true,
      data: response.data || [],
      error: null,
    };
  } catch (error: any) {
    console.error('UsersService: Error fetching learning history:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch learning history' },
    };
  }
};

/**
 * Create a new user
 */
export const createUser = async (
  userData: CreateUserDTO
): Promise<{
  success: boolean;
  data?: { userId: string; generatedPassword: string };
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<
      ApiResponse<{ userId: string; generatedPassword: string }>
    >('/admin/users', userData);

    return {
      success: response.success,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    console.error('UsersService: Error creating user:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to create user' },
    };
  }
};

/**
 * Update user
 */
export const updateUser = async (
  userId: string,
  userData: UpdateUserDTO
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.put<ApiResponse<null>>(
      `/admin/users/${userId}`,
      userData
    );

    return {
      success: response.success,
      message: response.message || 'User updated successfully',
      error: null,
    };
  } catch (error: any) {
    console.error('UsersService: Error updating user:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to update user' },
    };
  }
};

/**
 * Change user status
 */
export const changeUserStatus = async (
  userId: string,
  status: UserStatus
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.patch<ApiResponse<null>>(
      `/admin/users/${userId}/status`,
      { status }
    );

    return {
      success: response.success,
      message: response.message || 'User status updated successfully',
      error: null,
    };
  } catch (error: any) {
    console.error('UsersService: Error changing user status:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to change user status' },
    };
  }
};

/**
 * Change user role
 */
export const changeUserRole = async (
  userId: string,
  role: string
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.patch<ApiResponse<null>>(
      `/admin/users/${userId}/role`,
      { role }
    );

    return {
      success: response.success,
      message: response.message || 'User role updated successfully',
      error: null,
    };
  } catch (error: any) {
    console.error('UsersService: Error changing user role:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to change user role' },
    };
  }
};

/**
 * Set or clear the reports-to (direct manager) for a user.
 * Pass null to clear.
 */
export const setReportsTo = async (
  userId: string,
  reportsToId: string | null
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.patch<ApiResponse<null>>(
      `/admin/users/${userId}/reports-to`,
      { reportsToId }
    );
    return {
      success: response.success,
      message: response.message,
      error: null,
    };
  } catch (error: any) {
    console.error('UsersService: Error setting reports-to:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to update reports-to' },
    };
  }
};

/**
 * Reset user password
 */
export const resetUserPassword = async (
  userId: string
): Promise<{
  success: boolean;
  message?: string;
  data?: { newPassword: string };
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ newPassword: string }>>(
      `/admin/users/${userId}/reset-password`
    );

    return {
      success: response.success,
      message: response.message || 'Password reset successfully',
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    console.error('UsersService: Error resetting password:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to reset password' },
    };
  }
};

/**
 * Deactivate user
 */
export const deactivateUser = async (
  userId: string,
  reason: string
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<null>>(
      `/admin/users/${userId}/deactivate`,
      { reason }
    );

    return {
      success: response.success,
      message: response.message || 'User deactivated successfully',
      error: null,
    };
  } catch (error: any) {
    console.error('UsersService: Error deactivating user:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to deactivate user' },
    };
  }
};

/**
 * Reactivate user
 */
export const reactivateUser = async (
  userId: string
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<null>>(
      `/admin/users/${userId}/reactivate`
    );

    return {
      success: response.success,
      message: response.message || 'User reactivated successfully',
      error: null,
    };
  } catch (error: any) {
    console.error('UsersService: Error reactivating user:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to reactivate user' },
    };
  }
};

/**
 * Get deactivation log
 */
export const getDeactivationLogs = async (
  page: number = 1,
  limit: number = 10
): Promise<{
  success: boolean;
  data?: DeactivationLog[];
  pagination?: Pagination;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<DeactivationLog[]>>(
      `/admin/users/deactivation-log?page=${page}&limit=${limit}`
    );

    return {
      success: true,
      data: response.data || [],
      pagination: response.pagination,
      error: null,
    };
  } catch (error: any) {
    console.error('UsersService: Error fetching deactivation logs:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch deactivation logs' },
    };
  }
};

/**
 * Get departments for filter dropdown
 */
export const getDepartments = async (): Promise<{
  success: boolean;
  data?: string[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<string[]>>(
      '/admin/users/departments'
    );

    return {
      success: true,
      data: response.data || [],
      error: null,
    };
  } catch (error: any) {
    console.error('UsersService: Error fetching departments:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch departments' },
    };
  }
};

export interface EnrollmentCourse {
  id: number;
  name: string;
  description: string;
  level: string;
  lessons: number;
  duration: string;
  thumbnail: string;
  category: string;
  isEnrolled: number;
}

/**
 * Get courses for enrollment
 */
export const getCoursesForEnrollment = async (
  userId: string,
  params: {
    search?: string;
    page?: number;
    limit?: number;
  } = {}
): Promise<{
  success: boolean;
  data?: EnrollmentCourse[];
  pagination?: Pagination;
  error?: { message: string } | null;
}> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    queryParams.append('page', String(params.page || 1));
    queryParams.append('limit', String(params.limit || 10));

    const response = await httpClient.get<ApiResponse<EnrollmentCourse[]>>(
      `/admin/users/${userId}/courses?${queryParams.toString()}`
    );

    return {
      success: true,
      data: response.data || [],
      pagination: response.pagination,
      error: null,
    };
  } catch (error: any) {
    console.error('UsersService: Error fetching courses:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch courses' },
    };
  }
};

/**
 * Enroll user in a course
 */
export const enrollUserInCourse = async (
  userId: string,
  courseId: number
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<null>>(
      `/admin/users/${userId}/enroll`,
      { courseId }
    );

    return {
      success: response.success,
      message: response.message || 'User enrolled successfully',
      error: null,
    };
  } catch (error: any) {
    console.error('UsersService: Error enrolling user:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to enroll user' },
    };
  }
};

/**
 * Unenroll user from a course
 */
export const unenrollUserFromCourse = async (
  userId: string,
  courseId: number
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.delete<ApiResponse<null>>(
      `/admin/users/${userId}/enroll/${courseId}`
    );

    return {
      success: response.success,
      message: response.message || 'User unenrolled successfully',
      error: null,
    };
  } catch (error: any) {
    console.error('UsersService: Error unenrolling user:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to unenroll user' },
    };
  }
};

/**
 * Get admin logs for a user
 */
export const getAdminLogs = async (
  userId: string,
  page: number = 1,
  limit: number = 10
): Promise<{
  success: boolean;
  data?: AdminLog[];
  pagination?: Pagination;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<AdminLog[]>>(
      `/admin/users/${userId}/admin-logs?page=${page}&limit=${limit}`
    );

    return {
      success: true,
      data: response.data || [],
      pagination: response.pagination,
      error: null,
    };
  } catch (error: any) {
    console.error('UsersService: Error fetching admin logs:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch admin logs' },
    };
  }
};

/**
 * Bulk import users
 */
export const bulkImportUsers = async (
  users: BulkImportUser[]
): Promise<{
  success: boolean;
  data?: { success: number; failed: number; errors: Array<{ email: string; error: string }> };
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<
      ApiResponse<{ success: number; failed: number; errors: Array<{ email: string; error: string }> }>
    >('/admin/users/bulk-import', { users });

    return {
      success: true,
      data: response.data,
      message: response.message,
      error: null,
    };
  } catch (error: any) {
    console.error('UsersService: Error importing users:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to import users' },
    };
  }
};
